import { Drawer } from '@medusajs/ui';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { DEFAULT_COUNTRY_CODE } from '@lib/constants/country-code';
import { useLogout } from '@lib/query/mutations/customer';
import Button from '@modules/common/components/button';
import { Divider } from '@modules/common/components/divider';
import Text from '@modules/common/components/text';
import type { LayoutCustomerData } from '@marxbiotech/shared-types/bff';
import { useMobileNav } from './context';
import NavItem from './nav-item';
import type {
  CategoryHandleConfig,
  ProductCategory,
} from '../product-nav';

// Account nav item types
export interface AccountNavItem {
  label: string;
  href?: string;
  links?: { label: string; href: string }[];
}

const isGroupCategory = (handle: string, config: CategoryHandleConfig) =>
  [config.brands, config.od].includes(handle);

export interface MobileNavProps {
  customer: Pick<LayoutCustomerData, 'email'> | null;
  categories: ProductCategory[];
  categoryHandleConfig: CategoryHandleConfig;
  isLoggedIn: boolean;
  navItems: AccountNavItem[];
}

export const MobileNav = ({
  customer,
  categories,
  categoryHandleConfig,
  isLoggedIn,
  navItems,
}: MobileNavProps) => {
  const { isOpen, toggleDrawer, closeDrawer } = useMobileNav();
  const { countryCode } = useParams<{ countryCode: string }>();
  const code = countryCode || DEFAULT_COUNTRY_CODE;

  const { mutate: logout } = useLogout({
    onSuccess: () => {
      // 登出後 full page reload 確保狀態清除（與 account-nav/login-nav 一致）
      window.location.href = `/${code}`;
    },
  });

  // Product categories nav
  const normalizedCategories = useMemo(() => {
    const composeHref = (handles: string[]) =>
      `/${code}/store/${handles.join('/')}`;
    return categories
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .map((category: ProductCategory) => {
        const isGroup = isGroupCategory(category.handle, categoryHandleConfig);
        const href = isGroup ? '' : composeHref([category.handle]);
        return {
          title: category.name,
          href,
          'data-testid': `mobile-nav-category-${category.handle}`,
          childItems: category.category_children?.map(
            (child: ProductCategory) => {
              const childHref = isGroup
                ? composeHref([child.handle])
                : composeHref([category.handle, child.handle]);
              return {
                title: child.name,
                href: childHref,
                'data-testid': `mobile-nav-subcategory-${child.handle}`,
              };
            }
          ),
        };
      });
  }, [categories, categoryHandleConfig, code]);

  // Account nav
  const normalizedAccountNavData = navItems.map((item) => ({
    title: item.label,
    href: item.href ?? '',
    childItems: item.links?.map((link) => ({
      title: link.label,
      href: link.href,
    })) ?? [],
  }));

  const handleLogout = () => {
    closeDrawer();
    logout();
  };

  return (
    <Drawer open={isOpen} onOpenChange={toggleDrawer}>
      <Drawer.Content className="mobile-nav" data-testid="mobile-nav-drawer">
        <Drawer.Header>
          <Drawer.Title asChild><Text variant="title-bold"></Text></Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="overflow-auto">
          <div className="flex flex-col gap-4">
            {isLoggedIn ? (
              <div>
                <div className="text-grey-30">目前登入的帳戶</div>
                <div className="text-black-10 truncate">{customer?.email}</div>
              </div>
            ) : (
              <div className="mb-4">
                <Button
                  as="link"
                  href="/account"
                  onClick={closeDrawer}
                  fullWidth
                  className="mt-6 block text-center"
                  data-testid="mobile-nav-login-button"
                >
                  會員登入
                </Button>
              </div>
            )}
            {normalizedCategories.length > 0 && <Divider />}

            {normalizedCategories.map((item) => (
              <NavItem {...item} key={item.title || item.href} />
            ))}

            {isLoggedIn && (
              <>
                <Divider />

                <div className="text-black-10 font-bold">會員中心</div>
                {normalizedAccountNavData.map((item) => (
                  <div className="px-2" key={item.title || item.href}>
                    <NavItem {...item} />
                  </div>
                ))}
              </>
            )}
          </div>
        </Drawer.Body>
        {isLoggedIn && (
          <Drawer.Footer>
            <Button onClick={handleLogout} variant="outlined" fullWidth data-testid="mobile-nav-logout-button">
              登出
            </Button>
          </Drawer.Footer>
        )}
      </Drawer.Content>
    </Drawer>
  );
};
